// Thin wrapper over the GTM-injected Amplitude SDK + GTM dataLayer.
// We do not install the Amplitude package; it loads via the GTM container,
// so all calls are guarded against the SDK being absent (localhost, ad
// blockers, slow GTM bootstrap). The dataLayer.push is the canonical signal —
// GTM-side tags forward to Amplitude/GA from there — and the direct
// amplitude.* call is an immediate fast-path for when the SDK is already up.

import type { UserResponse } from 'src/openapi';
import {
  getStoredReferrerUrl,
  getStoredSourceUrl,
} from 'src/helpers/utmCapture';

interface AmplitudeIdentify {
  set: (key: string, value: unknown) => AmplitudeIdentify;
}

interface AmplitudeSDK {
  setUserId?: (userId: string | undefined) => void;
  identify?: (identify: AmplitudeIdentify) => void;
  Identify?: new () => AmplitudeIdentify;
  track?: (eventName: string, properties?: Record<string, unknown>) => void;
  reset?: () => void;
}

declare global {
  interface Window {
    amplitude?: AmplitudeSDK;
    dataLayer?: Array<Record<string, unknown>>;
  }
}

const pushDataLayer = (payload: Record<string, unknown>) => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

const buildUserProperties = (user: UserResponse): Record<string, unknown> => {
  const props: Record<string, unknown> = {
    user_type: user.user_type,
    created_at: user.created_at,
    signup_source_url: getStoredSourceUrl(),
  };
  const referrer = getStoredReferrerUrl();
  if (referrer) props.signup_referrer_url = referrer;
  return Object.fromEntries(
    Object.entries(props).filter(([, v]) => v !== undefined && v !== ''),
  );
};

// Every public helper swallows its own errors. Callers sit inside auth
// thunks; an analytics failure must never bubble up and abort login/logout.
export const identifyUser = (user: UserResponse | undefined): void => {
  try {
    if (!user?.id) return;
    const userId = String(user.id);
    const props = buildUserProperties(user);

    const amp = window.amplitude;
    if (amp?.setUserId) amp.setUserId(userId);
    if (amp?.identify && amp.Identify) {
      const identify = new amp.Identify();
      Object.entries(props).forEach(([k, v]) => identify.set(k, v));
      amp.identify(identify);
    }
    pushDataLayer({ event: 'user_identified', user_id: userId, ...props });
  } catch {
    // analytics best-effort
  }
};

export const resetUser = (): void => {
  try {
    window.amplitude?.reset?.();
    pushDataLayer({ event: 'user_logout' });
  } catch {
    // analytics best-effort
  }
};

export const trackEvent = (
  eventName: string,
  properties?: Record<string, unknown>,
): void => {
  try {
    window.amplitude?.track?.(eventName, properties);
    pushDataLayer({ event: eventName, ...(properties || {}) });
  } catch {
    // analytics best-effort
  }
};
